import { axiosInstance } from '../../utils/axios'
import { downloadFromApi } from '../../utils/downloadFile'
import type {
  BackfillResult,
  FlowBillingConfig,
  FlowBillingConfigInput,
  FlowBillingRecipient,
  FlowBillingRecipientInput,
  FlowBillingRunsPage,
  FlowBillingScheduleStatus,
  MissingMonth,
  PreviewParams,
  PreviewResult,
  PublishResult,
  StoredReport,
  UnpublishResult,
  RunNowResult,
  SendRunResult,
  TestSendResult,
} from '../../types/flowBilling'

/**
 * Admin flow-billing API.
 *
 * Same shape as systemHealthApi: a BASE_URL, an object of async functions, and
 * the {success, data} envelope unwrapped here so components never see it.
 */

interface ApiResponse<T> {
  success: boolean
  data: T
}

const BASE_URL = '/admin/flow-billing'

export const flowBillingApi = {
  getConfig: async (): Promise<FlowBillingConfig> => {
    const res = await axiosInstance.get<ApiResponse<FlowBillingConfig>>(`${BASE_URL}/config`)
    return res.data.data
  },

  getScheduleStatus: async (): Promise<FlowBillingScheduleStatus> => {
    const res = await axiosInstance.get<ApiResponse<FlowBillingScheduleStatus>>(
      `${BASE_URL}/config/schedule-status`
    )
    return res.data.data
  },

  updateConfig: async (input: FlowBillingConfigInput): Promise<FlowBillingConfig> => {
    const res = await axiosInstance.patch<ApiResponse<FlowBillingConfig>>(
      `${BASE_URL}/config`,
      input
    )
    return res.data.data
  },

  listRecipients: async (): Promise<FlowBillingRecipient[]> => {
    const res = await axiosInstance.get<ApiResponse<FlowBillingRecipient[]>>(
      `${BASE_URL}/recipients`
    )
    return res.data.data
  },

  createRecipient: async (input: FlowBillingRecipientInput): Promise<FlowBillingRecipient> => {
    const res = await axiosInstance.post<ApiResponse<FlowBillingRecipient>>(
      `${BASE_URL}/recipients`,
      input
    )
    return res.data.data
  },

  updateRecipient: async (
    id: number,
    input: Partial<FlowBillingRecipientInput>
  ): Promise<FlowBillingRecipient> => {
    const res = await axiosInstance.patch<ApiResponse<FlowBillingRecipient>>(
      `${BASE_URL}/recipients/${id}`,
      input
    )
    return res.data.data
  },

  deleteRecipient: async (id: number): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/recipients/${id}`)
  },

  listRuns: async (
    params: {
      page?: number
      limit?: number
      status?: string
      /** One month's versions, resolved in the configuration timezone. */
      year?: number
      month?: number
      includeDeleted?: boolean
    } = {}
  ) => {
    const res = await axiosInstance.get<ApiResponse<FlowBillingRunsPage>>(`${BASE_URL}/runs`, {
      params,
    })
    return res.data.data
  },

  /** A stored version's report, for showing it on the page. */
  getRunReport: async (runId: number): Promise<StoredReport> => {
    const res = await axiosInstance.get<ApiResponse<StoredReport>>(`${BASE_URL}/runs/${runId}/report`)
    return res.data.data
  },

  preview: async (params: PreviewParams = {}): Promise<PreviewResult> => {
    const res = await axiosInstance.post<ApiResponse<PreviewResult>>(`${BASE_URL}/preview`, params)
    return res.data.data
  },

  /** Streams the preview straight to the user's disk; records nothing. */
  downloadPreview: (params: PreviewParams, format: 'csv' | 'pdf') =>
    downloadPost(`${BASE_URL}/preview`, { ...params, format }, `preview.${format}`),

  testSend: async (
    params: PreviewParams & { to?: string } = {}
  ): Promise<TestSendResult> => {
    const res = await axiosInstance.post<ApiResponse<TestSendResult>>(
      `${BASE_URL}/test-send`,
      params
    )
    return res.data.data
  },

  downloadRunCsv: (runId: number) =>
    downloadFromApi(`${BASE_URL}/runs/${runId}/download.csv`, `report-${runId}.csv`),

  downloadRunPdf: (runId: number) =>
    downloadFromApi(`${BASE_URL}/runs/${runId}/download.pdf`, `report-${runId}.pdf`),

  /**
   * Make a generated run the district's current report for its period.
   *
   * Deliberately separate from re-send: publishing changes what the portal
   * shows and emails nobody, re-sending mails the stored file and changes
   * nothing.
   */
  publishRun: async (runId: number): Promise<PublishResult> => {
    const res = await axiosInstance.post<ApiResponse<PublishResult>>(
      `${BASE_URL}/runs/${runId}/publish`
    )
    return res.data.data
  },

  /** Closed months with flow readings and no report yet. Read-only. */
  missingMonths: async (): Promise<MissingMonth[]> => {
    const res = await axiosInstance.get<ApiResponse<{ months: MissingMonth[] }>>(
      `${BASE_URL}/runs/backfill/missing`
    )
    return res.data.data.months
  },

  /** Generate and publish those months, oldest first. Never emails. */
  backfill: async (params: { limit?: number } = {}): Promise<BackfillResult> => {
    const res = await axiosInstance.post<ApiResponse<BackfillResult>>(
      `${BASE_URL}/runs/backfill`,
      params
    )
    return res.data.data
  },

  /**
   * Mark a run nobody outside the admin panel has seen as deleted.
   *
   * Nothing is destroyed: the run and its files are kept and restoreRun brings
   * it back. The server refuses a sent, published or superseded run unless
   * `withdraw` takes it back from the group first.
   */
  deleteRun: async (runId: number, withdraw = false): Promise<void> => {
    // The flag travels in the body, not the path: it changes what the call is
    // allowed to destroy, and that belongs in the request rather than in a URL
    // somebody could retry from a browser history entry.
    await axiosInstance.delete(`${BASE_URL}/runs/${runId}`, { data: { withdraw } })
  },

  /** Undo a delete. The run returns as a working copy, not republished. */
  restoreRun: async (runId: number): Promise<void> => {
    await axiosInstance.post(`${BASE_URL}/runs/${runId}/restore`)
  },

  /** Take a published report back, restoring whichever version it replaced. */
  unpublishRun: async (runId: number): Promise<UnpublishResult> => {
    const res = await axiosInstance.post<ApiResponse<UnpublishResult>>(
      `${BASE_URL}/runs/${runId}/unpublish`
    )
    return res.data.data
  },

  /**
   * Email a stored version to the recipients and make it the month's report.
   * A month already sent needs a reason, because this goes out as a revision.
   */
  sendRun: async (runId: number, reason: string | null): Promise<SendRunResult> => {
    const res = await axiosInstance.post<ApiResponse<SendRunResult>>(`${BASE_URL}/runs/${runId}/send`, {
      reason,
    })
    return res.data.data
  },

  resendRun: async (runId: number): Promise<void> => {
    await axiosInstance.post(`${BASE_URL}/runs/${runId}/resend`)
  },

  /**
   * Run the report on demand, down the same path the scheduled job takes.
   *
   * No period runs the previous calendar month, which is what the schedule
   * would have done. sendEmail false records a real run and mails nobody.
   */
  run: async (
    params: PreviewParams & { reason?: string | null; sendEmail: boolean }
  ): Promise<RunNowResult> => {
    const res = await axiosInstance.post<ApiResponse<RunNowResult>>(`${BASE_URL}/runs/run`, params)
    return res.data.data
  },
}

/**
 * Preview downloads are a POST, so they cannot reuse downloadFromApi.
 *
 * A GET would be tidier, but preview takes a body and must remain
 * non-idempotent-looking: it is the one action that reads the UNSAVED form
 * state, and putting that in a query string invites it being bookmarked and
 * replayed against a different configuration.
 */
async function downloadPost(url: string, body: unknown, fallbackName: string): Promise<void> {
  const { saveBlob, filenameFrom } = await import('../../utils/downloadFile')
  try {
    const response = await axiosInstance.post(url, body, { responseType: 'blob' })
    saveBlob(response.data as Blob, filenameFrom(response.headers, fallbackName))
  } catch (error: any) {
    const data = error?.response?.data
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text())
        throw new Error(parsed?.error?.message || 'Preview download failed')
      } catch (parseError) {
        if (parseError instanceof Error && parseError.message !== 'Preview download failed') {
          throw new Error('Preview download failed')
        }
        throw parseError
      }
    }
    throw new Error(error?.message || 'Preview download failed')
  }
}
